import { useMutation, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { HiOutlineArrowDownTray, HiOutlineEye } from "react-icons/hi2";

import { ScanStatusBadge, SignatureStateBadge } from "./FileBadges";
import { SignedPreviewDialog } from "./FilePreviewDialog";
import { FileTypeIcon } from "./FileTypeIcon";
import { canPreview, scanStatusView } from "./fileLabels";
import { fileTypeLabel, formatBytes } from "./formatting";
import type { ShellServices } from "./services";
import { useDocumentFileSources } from "./useDocumentFileSources";
import { downloadErrorMessage, downloadSignedFile } from "./versionDownload";
import { Notice } from "./Notice";

function when(value: string | null | undefined) {
  return !value || Number.isNaN(Date.parse(value))
    ? ""
    : new Intl.DateTimeFormat("ru-RU", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(value));
}

/** Одно вложение: вид и действия не зависят от того, откуда файл пришёл. */
interface AttachmentRow {
  readonly key: string;
  readonly name: string;
  readonly mimeType: string | null;
  readonly size: number | null;
  readonly scanStatus: string;
  readonly signatureState?: string;
  /** Файл можно открыть в предпросмотре (иначе только скачивание). */
  readonly previewAllowed: boolean;
  readonly download: () => Promise<void>;
  readonly loadPreview: () => Promise<{
    url: string;
    mime_type?: string | null;
  }>;
}

function Attachment({ row }: { row: AttachmentRow }) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const scan = scanStatusView(row.scanStatus);
  const download = useMutation({ mutationFn: row.download });

  return (
    <li className="dw-attach__item">
      <FileTypeIcon mimeType={row.mimeType} />
      <div className="dw-attach__info">
        <strong>{row.name}</strong>
        <span>
          {fileTypeLabel(row.mimeType)} · {formatBytes(row.size ?? 0)}
        </span>
        {scan.releasable ? null : <ScanStatusBadge status={row.scanStatus} />}
        {row.signatureState === "signed" ? (
          <SignatureStateBadge state={row.signatureState} />
        ) : null}
      </div>
      <div className="dw-attach__actions">
        {scan.releasable && row.previewAllowed ? (
          <button
            type="button"
            className="button button--secondary button--compact"
            aria-label={`Открыть ${row.name}`}
            onClick={() => setPreviewOpen(true)}
          >
            <HiOutlineEye aria-hidden="true" />
            <span>Открыть</span>
          </button>
        ) : null}
        {scan.releasable ? (
          <button
            type="button"
            className="button button--secondary button--compact"
            aria-label={`Скачать ${row.name}`}
            disabled={download.isPending}
            onClick={() => download.mutate()}
          >
            <HiOutlineArrowDownTray aria-hidden="true" />
            <span>Скачать</span>
          </button>
        ) : (
          <p className="file-hint" data-blocked={row.scanStatus}>
            {scan.blockedReason}
          </p>
        )}
      </div>
      {download.isError ? (
        <Notice className="dw-attach__error">
          {downloadErrorMessage(download.error)}
        </Notice>
      ) : null}
      {previewOpen ? (
        <SignedPreviewDialog
          title={row.name}
          mimeType={row.mimeType ?? "application/octet-stream"}
          loadPreview={row.loadPreview}
          onClose={() => setPreviewOpen(false)}
        />
      ) : null}
    </li>
  );
}

interface Group {
  readonly key: string;
  readonly title: string;
  readonly subtitle: string;
  readonly rows: readonly AttachmentRow[];
}

/**
 * Все вложения документа на первом экране карточки.
 *
 * Два источника: файлы, загруженные в сам документ (например, при его создании),
 * и файлы из писем, сгруппированные по письму. PDF, Word и Excel открываются в
 * предпросмотре, остальное только скачивается.
 */
export function MailAttachments({
  documentId,
  services,
}: {
  documentId: string;
  services: ShellServices;
}) {
  const sources = useDocumentFileSources({
    services,
    documentId,
    discussionFiles: [],
    enabled: true,
  });
  const files = useQuery({
    queryKey: ["document-workflow", "documents", documentId, "files"],
    queryFn: () => services.documentWorkflow.documentFiles(documentId),
    retry: false,
  });
  const groups = useMemo<readonly Group[]>(() => {
    const time = (m: {
      received_at?: string | null;
      sent_at?: string | null;
    }) => Date.parse(m.received_at ?? m.sent_at ?? "") || 0;
    const uploaded = (files.data ?? []).flatMap((file): AttachmentRow[] => {
      const version = file.current_version;
      if (!version) return [];

      return [
        {
          key: `file:${file.id}`,
          name: file.display_name,
          mimeType: version.detected_mime,
          size: version.size,
          scanStatus: version.scan_status,
          signatureState: version.signature_state,
          previewAllowed:
            version.available_actions.includes("preview") &&
            canPreview(file.display_name, version.detected_mime),
          download: () =>
            downloadSignedFile(
              () => services.documentWorkflow.downloadVersion(version.id),
              version,
              file.display_name,
            ),
          loadPreview: () =>
            services.documentWorkflow.previewVersion(version.id),
        },
      ];
    });
    const mail = sources.sources.filter(
      (source) => source.origin === "email_attachment",
    );
    const mailGroups = sources.messages
      .map((message) => ({
        message,
        rows: mail
          .filter((source) => source.messageId === message.id)
          .map(
            (source): AttachmentRow => ({
              key: source.key,
              name: source.name,
              mimeType: source.mimeType,
              size: source.size,
              scanStatus: source.scanStatus,
              previewAllowed: canPreview(source.name, source.mimeType),
              download: () =>
                downloadSignedFile(
                  () =>
                    services.documentWorkflow.downloadMailAttachment(
                      source.attachmentId ?? "",
                    ),
                  { scan_status: source.scanStatus, sha256: source.sha256 },
                  source.name,
                ),
              loadPreview: () =>
                services.documentWorkflow.previewMailAttachment(
                  source.attachmentId ?? "",
                ),
            }),
          ),
      }))
      .filter((group) => group.rows.length > 0)
      .sort((a, b) => time(b.message) - time(a.message))
      .map(({ message, rows }): Group => {
        const who = message.from?.name || message.from?.email;

        return {
          key: message.id,
          title: `Из письма: ${message.subject || "без темы"}`,
          subtitle: [who, when(message.received_at ?? message.sent_at)]
            .filter(Boolean)
            .join(" · "),
          rows,
        };
      });

    return [
      ...(uploaded.length
        ? [
            {
              key: "uploaded",
              title: "Прикреплено к документу",
              subtitle: "",
              rows: uploaded,
            },
          ]
        : []),
      ...mailGroups,
    ];
  }, [files.data, sources.sources, sources.messages, services]);
  const total = groups.reduce((sum, group) => sum + group.rows.length, 0);
  const pending = files.isPending || sources.pending;
  const failed = files.isError || sources.mailFailed;
  const retry = () => {
    void files.refetch();
    sources.retry();
  };

  return (
    <section className="dw-attach" aria-labelledby="dw-attach-title">
      <h3 id="dw-attach-title">
        Вложения
        {total ? <span className="dw-attach__count">{total}</span> : null}
      </h3>
      {pending && total === 0 ? (
        <p aria-busy="true">Загружаем вложения…</p>
      ) : null}
      {failed ? (
        <Notice>
          Не удалось получить часть вложений.{" "}
          <button
            type="button"
            className="button button--text button--compact"
            onClick={retry}
          >
            Повторить
          </button>
        </Notice>
      ) : null}
      {!pending && !failed && total === 0 ? (
        <p className="dw-secondary">К документу ничего не приложено.</p>
      ) : null}
      {groups.map((group) => (
        <div key={group.key} className="dw-attach__group">
          {groups.length > 1 ? (
            <p className="dw-attach__from">
              {group.title}
              {group.subtitle ? <span>{group.subtitle}</span> : null}
            </p>
          ) : null}
          <ul className="dw-attach__list">
            {group.rows.map((row) => (
              <Attachment key={row.key} row={row} />
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
}
